import { APP_PATH_PREFIX } from './constants/common';

export const CREATE_PROJECT_LOCAL_TEMPLATE_ID_SEARCH_PARAMS_KEY = 'cp_lt_id';
export const CREATE_PROJECT_GLOBAL_TEMPLATE_ID_SEARCH_PARAMS_KEY = 'cp_gt_id';
export const CREATE_PROJECT_DEFAULT_OPEN_SEARCH_PARAMS_KEY = 'cp_do';

export const getProjectDetailsPath = (projectId: string) =>
  `${APP_PATH_PREFIX.PM}/work/projects/details/${projectId}`;

export const getProjectRecurringSeriesTemplateEditorPath = (
  recurringProjectSeriesTemplateId: string,
) =>
  `${APP_PATH_PREFIX.PM}/work/projects/rste/${recurringProjectSeriesTemplateId}`;

export const getEditProjectRecurringSeriesTemplateEditorPath = (
  ...params: Parameters<typeof getProjectRecurringSeriesTemplateEditorPath>
) => getProjectRecurringSeriesTemplateEditorPath(...params) + '/ep';

export const getProjectTaskRoutePath = ({
  projectId,
  taskId,
}: {
  projectId: string;
  taskId: string;
}) => `${getProjectDetailsPath(projectId)}/tasks/${taskId}`;

export const getCreateProposalPath = ({
  templateId,
  globalTemplateId,
  clientId,
  contactId,
  serviceIds,
}: {
  templateId?: string;
  globalTemplateId?: string;
  clientId?: string | null;
  contactId?: string;
  serviceIds?: string[];
}) => {
  const queryParams = new URLSearchParams();

  if (clientId) {
    queryParams.append('clientId', clientId);
  }

  if (contactId) {
    queryParams.append('contactId', contactId);
  }

  if (serviceIds) {
    serviceIds.forEach((serviceId) => {
      queryParams.append('serviceIds', serviceId);
    });
  }

  if (templateId) {
    queryParams.append('templateId', templateId);
  }

  if (globalTemplateId) {
    queryParams.append('globalTemplateId', globalTemplateId);
  }

  return `${APP_PATH_PREFIX.PM}/proposals/create?${queryParams.toString()}`;
};

export const getEditProposalPath = (proposalId: string) =>
  `${APP_PATH_PREFIX.PM}/proposals/${proposalId}`;

export const getProposalDetailsPath = (proposalId: string) =>
  `${APP_PATH_PREFIX.PM}/proposals/details/${proposalId}`;

export const getCreateProjectWithClientPath = (clientId: string) =>
  `${APP_PATH_PREFIX.PM}/clients/list/${clientId}/projects/list?${CREATE_PROJECT_DEFAULT_OPEN_SEARCH_PARAMS_KEY}=1`;

export const getCreateProjectPath = ({
  localTemplateId,
  globalTemplateId,
}: {
  localTemplateId?: string;
  globalTemplateId?: string;
}) => {
  const queryParams = new URLSearchParams();

  if (localTemplateId) {
    queryParams.append(
      CREATE_PROJECT_LOCAL_TEMPLATE_ID_SEARCH_PARAMS_KEY,
      localTemplateId,
    );
  }

  if (globalTemplateId) {
    queryParams.append(
      CREATE_PROJECT_GLOBAL_TEMPLATE_ID_SEARCH_PARAMS_KEY,
      globalTemplateId,
    );
  }

  queryParams.append(CREATE_PROJECT_DEFAULT_OPEN_SEARCH_PARAMS_KEY, '1');

  return `${APP_PATH_PREFIX.PM}/work/projects/list?${queryParams.toString()}`;
};

export const getCreateEditTaskStatusListTemplatePath = (
  statusListTemplateId?: string,
): string =>
  `${APP_PATH_PREFIX.PM}/settings/workflow/status-lists` +
  (statusListTemplateId ? `/${statusListTemplateId}` : '');

export const getCreateDealPathWithClientOrContact = (
  contactId?: string,
  clientId?: string,
): string => {
  const queryParams = new URLSearchParams();

  if (contactId) {
    queryParams.append('contact_id', contactId);
  }

  if (clientId) {
    queryParams.append('client_id', clientId);
  }

  return `${APP_PATH_PREFIX.PM}/deals/board/create?${queryParams.toString()}`;
};

export const getClientBillingPath = ({
  clientId,
  goToAllServices,
}: {
  clientId: string;
  goToAllServices?: boolean;
}) => {
  return `${APP_PATH_PREFIX.PM}/clients/list/${clientId}/billing/pending-items${
    goToAllServices ? '#client-pending-all-services' : ''
  }`;
};

export const getEmailIntegrationOnboardingPath = () =>
  `${APP_PATH_PREFIX.PM}/inbox/emails-v2/onboarding`;

export const getEmailIntegrationViewInboxPath = (integrationId: string) =>
  `${APP_PATH_PREFIX.PM}/inbox/emails-v2/view/${integrationId}/inbox`;

export const getPmClientPortalAuthPath = () =>
  `${APP_PATH_PREFIX.PM_CLIENT}/auth`;
export const getPmClientPortalAppPath = () =>
  `${APP_PATH_PREFIX.PM_CLIENT}/app`;

export const getSubscriptionPlanRoute = `${APP_PATH_PREFIX.PM}/settings/subscription/plans`;

export const getIntegrationRoute = `${APP_PATH_PREFIX.PM}/settings/integrations`;

export const getProjectTemplateRoute = `${APP_PATH_PREFIX.PM}/templates/projects`;

export const getInvoicesPath = ({
  recurringInvoiceId,
  clientId,
}: {
  recurringInvoiceId?: string;
  clientId?: string;
}) => {
  const basePath = `${APP_PATH_PREFIX.PM}/billing/invoices`;
  const queryParams = new URLSearchParams();

  if (recurringInvoiceId) {
    queryParams.append('recurring_invoice_id', recurringInvoiceId);
  }

  if (clientId) {
    queryParams.append('client_id', clientId);
  }

  if (queryParams.toString()) {
    return `${basePath}?${queryParams.toString()}`;
  }

  return basePath;
};

export const getClientWorkSpaceInvoicesPath = ({
  recurringInvoiceId,
  clientId,
}: {
  recurringInvoiceId?: string;
  clientId: string;
}) => {
  const basePath = `${APP_PATH_PREFIX.PM}/clients/list/${clientId}/billing/invoices`;
  const queryParams = new URLSearchParams();

  if (recurringInvoiceId) {
    queryParams.append('recurring_invoice_id', recurringInvoiceId);
  }

  if (queryParams.toString()) {
    return `${basePath}?${queryParams.toString()}`;
  }

  return basePath;
};

export const getSettingsBillingInvoicingPath = () =>
  `${APP_PATH_PREFIX.PM}/settings/billing/invoicing`;

export const getJoinViaReferralAbsolutePath = (referralId: string) =>
  `${window.location.origin}/join?via=${referralId}`;

export const getJoinViaReferralToSignupPath = (referralId: string) => {
  const queryParams = new URLSearchParams();

  if (referralId) {
    queryParams.append('utm_campaign', 'referral');
    queryParams.append('utm_id', referralId);
    queryParams.append('utm_source', 'cone');
    queryParams.append('utm_medium', 'cone');
  }

  return `/auth${APP_PATH_PREFIX.P2P}/signup?${queryParams.toString()}`;
};

export const getInvoiceByIdPath = (invoiceId: string) =>
  `${APP_PATH_PREFIX.PM}/billing/invoices/${invoiceId}`;
